/**
 * DocumentDownload Component
 * Reusable component for displaying and downloading documents
 */

import { useState } from 'react';
import { Download, Loader2, FileText } from 'lucide-react';
import { downloadFile } from '../../src/utils/download.utils';

export interface Document {
  file_id: string;
  file_name?: string;
  name?: string;
  document_name?: string;
  file_type?: string;
  type?: string;
  document_type?: string;
  uploaded_at?: string;
  upload_date?: string;
  size?: number;
}

interface DocumentDownloadProps {
  document: Document;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  showIcon?: boolean;
  className?: string;
}

/**
 * Single document download button
 */
export function DocumentDownloadButton({
  document,
  onError,
  onSuccess,
  showIcon = true,
  className = ''
}: DocumentDownloadProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileName = document.file_name || document.name || document.document_name || 'document';

  const handleDownload = async () => {
    if (isDownloading) return; // Prevent multiple clicks

    setIsDownloading(true);
    setProgress(0);

    await downloadFile(document.file_id, fileName, {
      onProgress: (prog) => {
        setProgress(Math.round(prog));
      },
      onError: (error) => {
        setIsDownloading(false);
        setProgress(0);
        if (onError) onError(error);
      },
      onSuccess: () => {
        setIsDownloading(false);
        setProgress(100);
        if (onSuccess) onSuccess();
        // Reset progress after a short delay
        setTimeout(() => setProgress(0), 2000);
      }
    });
  };

  return (
    <button
      onClick={handleDownload}
      disabled={isDownloading}
      className={`text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      title={isDownloading ? `Downloading... ${progress}%` : `Download ${fileName}`}
    >
      {isDownloading ? (
        <span className="flex items-center gap-1">
          <Loader2 className="h-4 w-4 animate-spin" />
          {progress > 0 && <span className="text-xs">{progress}%</span>}
        </span>
      ) : showIcon ? (
        <Download className="h-4 w-4" />
      ) : (
        'Download'
      )}
    </button>
  );
}

interface DocumentListProps {
  documents: Document[];
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  className?: string;
  showEmptyMessage?: boolean;
  emptyMessage?: string;
}

/**
 * Document list with download functionality
 */
export function DocumentList({
  documents,
  onError,
  onSuccess,
  className = '',
  showEmptyMessage = true,
  emptyMessage = 'No documents available'
}: DocumentListProps) {
  if (documents.length === 0 && showEmptyMessage) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div className={`border rounded-lg overflow-hidden ${className}`}>
      <table className="w-full">
        <thead className="bg-muted">
          <tr>
            <th className="px-4 py-2 text-left text-sm font-medium">Documents</th>
            <th className="px-4 py-2 text-right text-sm font-medium w-16"></th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc, index) => {
            const fileName = doc.file_name || doc.name || doc.document_name || 'Unnamed Document';
            return (
              <tr key={index} className="border-t hover:bg-muted/50">
                <td className="px-4 py-2 text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  {fileName}
                </td>
                <td className="px-4 py-2 text-right">
                  {doc.file_id && (
                    <DocumentDownloadButton
                      document={doc}
                      onError={onError}
                      onSuccess={onSuccess}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface DocumentGridProps {
  documents: Document[];
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  className?: string;
}

/**
 * Document grid view with download functionality
 */
export function DocumentGrid({
  documents,
  onError,
  onSuccess,
  className = ''
}: DocumentGridProps) {
  if (documents.length === 0) {
    return <p className="text-sm text-muted-foreground">No documents available</p>;
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
      {documents.map((doc, index) => {
        const fileName = doc.file_name || doc.name || doc.document_name || 'Unnamed Document';
        return (
          <div
            key={index}
            className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" title={fileName}>
                    {fileName}
                  </p>
                  {doc.uploaded_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(doc.uploaded_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              {doc.file_id && (
                <DocumentDownloadButton
                  document={doc}
                  onError={onError}
                  onSuccess={onSuccess}
                  className="shrink-0"
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
